<template>
  <b-container fluid class="reports-filter">
    <b-row
      class="reports-filter__row"
      cols="1"
      :cols-md="displayHandler"
    >
      <b-col>
        <tag-options-input
          class="mb-3"
          :label="$t('entities.company')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="companies"
          :selected-items.sync="selectedCompanies"
          :options-custom-label="$t('reports.filters.options.all-companies')"
          :loading="loadingCompanies"
          :is-required-field="isCompanyFieldRequired"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateCompanies"
          @remove-item="onRemoveCompany"
        />
      </b-col>
      <b-col
        v-if="userCompaniesHaveRegional"
      >
        <tag-options-input
          class="mb-3"
          :label="$t('entities.regional')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="regional"
          :selected-items.sync="selectedRegional"
          :is-required-field="isRegionalFieldRequired"
          :options-custom-label="$t('reports.filters.options.all-regional')"
          :disabled="!(selectedCompanies || []).length"
          :loading="loadingRegional"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateRegional"
          @remove-item="onRemoveRegional"
        />
      </b-col>
      <b-col>
        <tag-options-input
          class="mb-3"
          :label="$t('entities.installation')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="installations"
          :selected-items.sync="selectedInstallations"
          :options-custom-label="$t('reports.filters.options.all-installations')"
          :disabled="!(selectedCompanies || []).length"
          :loading="loadingInstallations"
          :is-required-field="isInstallationFieldRequired"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateInstallation"
          @remove-item="onRemoveInstallation"
        />
      </b-col>
      <b-col
        v-if="hasTypeField"
      >
        <tag-options-input
          class="mb-3"
          :label="$t('entities.type')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="filteredAssetTypes"
          :selected-items.sync="selectedAssetTypes"
          :options-custom-label="$t('reports.filters.options.all-types')"
          :disabled="!(selectedInstallations || []).length"
          :loading="loadingAssets"
          :is-required-field="isTypeFieldRequired"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateAssetType"
          @remove-item="onRemoveAssetType"
          @add-item="setAssetsByAssetType"
          @add-all="setAssetsByAssetType"
        />
      </b-col>
      <b-col
        v-if="hasAssetField"
      >
        <tag-options-input
          class="mb-3"
          :label="$t('entities.asset')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="assets"
          :selected-items.sync="selectedAssets"
          :options-custom-label="$t('reports.filters.options.all-assets')"
          :disabled="isAssetsDisabled"
          :loading="loadingAssets"
          :is-required-field="isAssetFieldRequired"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateAssets"
          @remove-item="onRemoveAsset"
        />
      </b-col>
      <b-col
        v-if="hasSensorField"
      >
        <tag-options-input
          class="mb-3"
          :label="$t('entities.sensor')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="sensors"
          :selected-items.sync="selectedSensors"
          :options-custom-label="$t('reports.filters.options.all-sensors')"
          :disabled="!(selectedAssets || []).length"
          :loading="loadingSensors"
          :is-required-field="isSensorFieldRequired"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateSensors"
        />
      </b-col>
      <b-col
        v-if="hasModuleField"
      >
        <tag-options-input
          class="mb-3"
          :label="$t('entities.module')"
          :tag-label="'name'"
          :track-tag-by="'id'"
          :tags="modules"
          :selected-items.sync="selectedModules"
          :options-custom-label="$t('reports.filters.options.all-modules')"
          :disabled="!(selectedAssets || []).length"
          :loading="loadingModules"
          :is-required-field="isModuleFieldRequired"
          :dropdown-direction="dropdownDirection"
          @update:selectedItems="onUpdateModules"
        />
      </b-col>
      <slot></slot>
    </b-row>
  </b-container>
</template>

<script>
import uniq from 'lodash/uniq';
import {
  AssetTypes,
} from 'sigma-mappings';
import CompanyService from '@/domain/services/company';
import RegionalService from '@/domain/services/regional';
import InstallationService from '@/domain/services/installation';
import AssetService from '@/domain/services/asset';
import ModuleService from '@/domain/services/module';
import globalEventBus from '@/services/globalEventBus';
import { events } from '@/domain/services/generalPanel/reports/localEventBus';
import TagOptionsInput from '@/components/inputs/TagOptionsInput';
import { assetsTypesIds } from '@/domain/data/installation/assets';

export default {
  name: 'ReportsFilter',
  props: {
    filter: {
      type: Object,
      default: () => ({}),
    },
    filterObjects: {
      type: Object,
      default: () => ({}),
    },
    isCompanyFieldRequired: {
      type: Boolean,
      default: false,
    },
    isRegionalFieldRequired: {
      type: Boolean,
      default: false,
    },
    isInstallationFieldRequired: {
      type: Boolean,
      default: false,
    },
    isAssetFieldRequired: {
      type: Boolean,
      default: false,
    },
    isSensorFieldRequired: {
      type: Boolean,
      default: false,
    },
    isModuleFieldRequired: {
      type: Boolean,
      default: false,
    },
    isTypeFieldRequired: {
      type: Boolean,
      default: false,
    },
    hasTypeField: {
      type: Boolean,
      default: false,
    },
    hasAssetField: {
      type: Boolean,
      default: true,
    },
    hasSensorField: {
      type: Boolean,
      default: false,
    },
    hasModuleField: {
      type: Boolean,
      default: false,
    },
    dropdownDirection: {
      type: String,
      default: '',
    },
    displayFieldsInLine: {
      type: Boolean,
      default: false,
    },
    hasAllFiltersSelected: {
      type: Boolean,
      default: false,
    },
  },
  components: { TagOptionsInput },
  data() {
    return {
      companies: [],
      selectedCompanies: [],
      regional: [],
      selectedRegional: [],
      installations: [],
      selectedInstallations: [],
      assets: [],
      sensors: [],
      modules: [],
      filteredAssetTypes: [],
      assetTypes: [
        {
          id: assetsTypesIds[AssetTypes.TRANSFORMER],
          key: AssetTypes.TRANSFORMER,
          name: this.$t('enums.asset-info.asset-type.transformer'),
        },
        {
          id: assetsTypesIds[AssetTypes.DISCONNECTOR],
          key: AssetTypes.DISCONNECTOR,
          name: this.$t('enums.asset-info.asset-type.disconnector'),
        },
        {
          id: assetsTypesIds[AssetTypes.CIRCUIT_BREAKER],
          key: AssetTypes.CIRCUIT_BREAKER,
          name: this.$t('enums.asset-info.asset-type.circuit-breaker'),
        },
      ],
      selectedAssetTypes: [],
      selectedAssets: [],
      selectedSensors: [],
      selectedModules: [],
      loadingAssets: false,
      loadingSensors: false,
      loadingModules: false,
      loadingInstallations: false,
      loadingRegional: false,
      loadingCompanies: false,
      userCompaniesHaveRegional: true,
    };
  },
  computed: {
    displayHandler() {
      return this.hasTypeField || this.displayFieldsInLine ? '1' : '2';
    },
    isAssetsDisabled() {
      return (this.hasTypeField && !(this.selectedAssetTypes || []).length)
        || !(this.selectedInstallations || []).length;
    },
  },
  created() {
    this.getCompanies();
    this.fillFilters();
    this.initLocalBus();
  },
  beforeDestroy() {
    this.closeLocalBus();
  },
  methods: {
    initLocalBus() {
      globalEventBus.on(events.CLEAR_REPORTS_FILTER, this.clearAllFilters, this);
    },
    closeLocalBus() {
      globalEventBus.off(events.CLEAR_REPORTS_FILTER, this.clearAllFilters, this);
    },
    fillFilters() {
      if (!this.filterObjects) {
        return;
      }

      this.fillSelectedFilters();

      this.onUpdateCompanies();
      this.onUpdateRegional();
      this.onUpdateInstallation();
      this.onUpdateAssets();
      this.onUpdateSensors();
      this.onUpdateModules();
    },
    fillSelectedFilters() {
      this.selectedCompanies = this.filterObjects?.companies || [];
      this.selectedInstallations = this.filterObjects?.installations || [];
      this.selectedRegional = this.filterObjects?.regional || [];
      this.selectedAssets = this.filterObjects?.assets || [];
      this.selectedSensors = this.filterObjects?.sensors || [];
      this.selectedModules = this.filterObjects?.modules || [];
    },
    clearAllFilters() {
      this.selectedCompanies = [];
      this.onRemoveAllCompanies();
    },
    updateFilter() {
      const newFilter = {
        companiesIds: this.mapArrayIds(this.selectedCompanies),
        regionalIds: this.mapArrayIds(this.selectedRegional),
        installationsIds: this.mapArrayIds(this.selectedInstallations),
        assetsIds: this.mapArrayIds(this.selectedAssets),
        assetTypes: this.mapArrayIds(this.selectedAssetTypes),
        sensorsIds: this.mapArrayIds(this.selectedSensors),
        modulesIds: this.mapArrayIds(this.selectedModules),
      };
      this.$emit('update:filter', { ...this.filter, ...newFilter });
      this.updateFilterObjects();
    },
    updateFilterObjects() {
      const newFilter = {
        companies: this.selectedCompanies,
        regional: this.selectedRegional,
        installations: this.selectedInstallations,
        assets: this.selectedAssets,
        assetTypes: this.selectedAssetTypes,
        sensors: this.selectedSensors,
        modules: this.selectedModules,
      };
      this.$emit('update:filter-objects', { ...this.filter, ...newFilter });
    },
    onRemoveAllCompanies() {
      this.selectedRegional = [];
      this.selectedInstallations = [];
      this.selectedAssets = [];
      this.selectedAssetTypes = [];
      this.selectedSensors = [];
      this.selectedModules = [];
      this.updateFilter();
    },
    onUpdateCompanies() {
      if (!(this.selectedCompanies || []).length) {
        this.onRemoveAllCompanies();
      }

      this.checkUserCompaniesRegional();
      this.getRegional();
      this.setSelectedAssetTypesByAssets(this.selectedAssets);

      const searchByLastCompany = (this.selectedRegional || []).length > 0;
      this.getInstallations(searchByLastCompany);

      this.updateFilter();
      this.updateFilterObjects();
    },
    filterRegionalByCompanies() {
      return (this.selectedRegional || []).filter(
        regional => regional && regional.company
          && (this.selectedCompanies || [])
            .some(company => company
              && company.id
              && regional.company.id
              && company.id === regional.company.id),
      );
    },
    onRemoveCompany() {
      this.selectedRegional = this.filterRegionalByCompanies();
      this.onRemoveRegional();
    },
    onRemoveAllRegional() {
      this.selectedInstallations = this.filterInstallationsByParents(this.selectedInstallations);
      this.selectedAssets = this.filterAssetsByInstallations();
      this.setSelectedAssetTypesByAssets(this.selectedAssets);
      this.updateFilter();
    },
    onUpdateRegional() {
      if (!(this.selectedRegional || []).length) {
        this.onRemoveAllRegional();
      }
      this.getInstallations(false);
      this.updateFilter();
    },
    installationHasSomeRegionalSelected(installation) {
      const installationRegionalId = (installation.regional || {}).id;
      return this.selectedRegional?.some(
        regional => regional.id?.toUpperCase() === installationRegionalId?.toUpperCase()
          || !installationRegionalId,
      );
    },
    shouldNotConsiderRegionalByInstallation(installation) {
      return !(this.selectedRegional || []).length && !(installation.regional || {}).id;
    },
    installationHasRegionalSelected(installation) {
      return this.shouldNotConsiderRegionalByInstallation(installation)
        || this.installationHasSomeRegionalSelected(installation);
    },
    installationHasCompanySelected(installation) {
      return this.selectedCompanies?.some(
        company => company.id?.toUpperCase() === installation.company.id?.toUpperCase(),
      );
    },
    filterInstallationsByParents(installations) {
      return installations.filter(
        installation => this.installationHasRegionalSelected(installation)
          && this.installationHasCompanySelected(installation),
      );
    },
    onRemoveRegional() {
      this.selectedInstallations = this.filterInstallationsByParents(this.selectedInstallations);
      this.onUpdateRegional();
      this.onRemoveInstallation();
      this.updateFilter();
    },
    onRemoveAllInstallations() {
      this.selectedAssets = [];
      this.selectedAssetTypes = [];
      this.selectedSensors = [];
      this.selectedModules = [];
      this.updateFilter();
    },
    async onUpdateInstallation() {
      if (!(this.selectedInstallations || []).length) {
        this.onRemoveAllInstallations();
      }
      await this.getAssets();
      this.setSelectedAssetTypesByAssets(this.assets);
      this.updateFilter();
    },
    filterAssetsByInstallations() {
      return this.selectedAssets.filter(
        asset => this.selectedInstallations?.some(
          installation => installation.id?.toUpperCase() === asset.installation?.id?.toUpperCase(),
        ),
      );
    },
    filterAssetsByType(assets = []) {
      return assets.filter(
        asset => this.selectedAssetTypes?.some(
          assetType => assetType.id?.toUpperCase() === (asset.assetType || {}).id?.toUpperCase(),
        ),
      );
    },
    onRemoveInstallation() {
      this.selectedAssets = this.filterAssetsByInstallations();
      this.onRemoveAsset();
      this.onUpdateInstallation();
    },
    onRemoveAssetType() {
      this.assets = this.filterAssetsByType(this.assets);
      this.selectedAssets = this.filterAssetsByType(this.selectedAssets);
      this.onUpdateAssets();
    },
    onUpdateAssetType() {
      if (!(this.selectedAssetTypes || []).length) {
        this.onRemoveAllAssetType();
      }
      this.onUpdateAssets();
    },
    onRemoveAllAssetType() {
      this.selectedAssets = [];
      this.updateFilter();
    },
    async onUpdateAssets() {
      if (!(this.selectedAssets || []).length) {
        this.onRemoveAllSensors();
        this.onRemoveAllModules();
      }

      if (this.hasSensorField) {
        await this.getSensors();
      }

      if (this.hasModuleField) {
        await this.getModules();
      }

      this.updateFilter();
    },
    filterSensorByAssets() {
      const assetsIds = this.mapArrayIds(this.selectedAssets);
      return this.selectedSensors.filter(
        sensor => sensor.assetsIds?.some(
          id => (assetsIds || []).includes((id || '').toUpperCase()),
        ),
      );
    },
    filterModulesByAssets() {
      const assetsIds = this.mapArrayIds(this.selectedAssets);

      return this.selectedModules.filter(
        module => module.assetsIds?.map(id => id?.toUpperCase())
        .some(
          id => (assetsIds || []).includes((id || '').toUpperCase()),
        ),
      );
    },
    onRemoveAsset() {
      if (this.hasSensorField) {
        this.selectedSensors = this.filterSensorByAssets();
      }

      if (this.hasModuleField) {
        this.selectedModules = this.filterModulesByAssets();
      }

      this.onUpdateAssets();
    },
    getCompanies() {
      this.loadingCompanies = true;
      CompanyService.getCompanies()
        .then((response) => {
          this.companies = response;
          this.selectedCompanies = this.syncSelectedWithList(
            this.selectedCompanies,
            this.companies,
          );
          this.checkUserCompaniesRegional();
        })
        .finally(() => {
          this.loadingCompanies = false;
        });
    },
    getRegional() {
      if (!(this.selectedCompanies || []).length) return;
      this.loadingRegional = true;
      const companiesIds = this.mapArrayIds(this.selectedCompanies);
      RegionalService.getRegional(companiesIds)
        .then((response) => {
          this.regional = response;
          this.selectedRegional = this.syncSelectedWithList(
            this.selectedRegional,
            this.regional,
          );
        })
        .finally(() => {
          this.loadingRegional = false;
        });
    },
    getLastCompanySelectedId() {
      if (!(this.selectedCompanies || []).length) return '';
      return this.selectedCompanies[(this.selectedCompanies || []).length - 1].id;
    },
    mapArrayIds(array = []) {
      return array.map(item => item.id?.toUpperCase());
    },
    syncSelectedWithList(selectedList, availableList) {
      if (!(selectedList || []).length || !(availableList || []).length) {
        return selectedList;
      }
      return selectedList.map((selected) => {
        const match = availableList.find(
          item => item.id?.toUpperCase() === selected.id?.toUpperCase(),
        );
        return match || selected;
      });
    },
    getRegionalIdsBasedOnCompanyId(companyId) {
      return this.selectedRegional
        .filter(regional => regional.company.id?.toUpperCase() === companyId?.toUpperCase())
        .map(regional => regional.id);
    },
    getInstallationsWithoutRegional(installationsList) {
      return installationsList.filter(installation => !(installation.regional || {}).id);
    },
    getUniqueObjectsArrayById(array) {
      return [
        ...new Map((array).map(item => [item.id, item])).values(),
      ];
    },
    sortInstallationsByName() {
      this.installations.sort((a, b) => a.name.localeCompare(b.name));
    },
    resolveGetInstallations(response) {
      const allInstallations = [...this.installations, ...response];
      const installationsWithoutRegional = this.getInstallationsWithoutRegional(allInstallations);
      const installationsWithAndWithoutRegional = [
        ...allInstallations,
        ...installationsWithoutRegional,
      ];
      const currentInstallations = (this.selectedRegional || []).length
        ? installationsWithAndWithoutRegional
        : installationsWithoutRegional;
      this.installations = this.getUniqueObjectsArrayById(currentInstallations);
      this.installations = this.filterInstallationsByParents(this.installations);
      this.sortInstallationsByName();
      this.selectedInstallations = this.syncSelectedWithList(
        this.selectedInstallations,
        this.installations,
      );
    },
    getInstallationsRequestParams(searchByLastCompany) {
      if (searchByLastCompany) {
        const lastCompany = this.getLastCompanySelectedId();
        return {
          companiesIds: [lastCompany],
          regionalIds: this.getRegionalIdsBasedOnCompanyId(lastCompany),
        };
      }
      return {
        companiesIds: this.mapArrayIds(this.selectedCompanies),
        regionalIds: this.mapArrayIds(this.selectedRegional),
      };
    },
    getInstallations(searchByLastCompany) {
      if (!(this.selectedCompanies || []).length) return;
      const params = this.getInstallationsRequestParams(searchByLastCompany);
      this.loadingInstallations = true;
      InstallationService.getInstallationsByCompaniesOrRegional(params)
        .then((response) => {
          this.resolveGetInstallations(response);
        })
        .finally(() => {
          this.loadingInstallations = false;
        });
    },
    setSelectedAssetTypesByAssets(assets) {
      if (!this.hasTypeField || !(this.selectedInstallations || []).length) {
        this.selectedAssetTypes = [];
        return;
      }
      const uniqueAssetTypesIds = uniq(
        assets.map(it => it.assetType.id),
      );
      this.filteredAssetTypes = this.assetTypes.filter(
        type => uniqueAssetTypesIds?.some(
          assetTypeId => assetTypeId?.toUpperCase() === type.id?.toUpperCase(),
        ),
      );
      this.selectedAssetTypes = this.filteredAssetTypes;
    },
    async setAssetsByAssetType() {
      await this.getAssets();
      this.assets = this.filterAssetsByType(this.assets);
    },
    getAssets() {
      const installationsIds = this.mapArrayIds(this.selectedInstallations);
      this.loadingAssets = true;
      return AssetService.getAssetsByInstallations({
        installationsIds,
        assetTypeId: '',
      })
        .then((response) => {
          this.assets = response.map(asset => ({
            ...asset,
            name: `${asset.installation.name} | ${asset.name}`,
          }));
          this.selectedAssets = this.syncSelectedWithList(
            this.selectedAssets,
            this.assets,
          );
        })
        .finally(() => {
          this.loadingAssets = false;
        });
    },
    checkUserCompaniesRegional() {
      const allUserCompaniesIds = (this.selectedCompanies || []).length
        ? this.mapArrayIds(this.selectedCompanies)
        : this.mapArrayIds(this.companies);
      RegionalService.getRegional(allUserCompaniesIds)
        .then((response) => {
          this.userCompaniesHaveRegional = !!(response || []).length;
          this.$emit('user-companies-have-regional', this.userCompaniesHaveRegional);
        });
    },
    getSensors() {
      const assetsIds = this.mapArrayIds(this.selectedAssets);
      this.loadingSensors = true;
      return ModuleService.getSensorsByAssetIds({ assetsIds })
        .then((response) => {
          this.sensors = response;
          this.selectedSensors = this.syncSelectedWithList(
            this.selectedSensors,
            this.sensors,
          );
        })
        .finally(() => {
          this.loadingSensors = false;
        });
    },
    onRemoveAllSensors() {
      this.selectedSensors = [];
      this.updateFilter();
    },
    onUpdateSensors() {
      this.updateFilter();
    },
    getModules() {
      const assetsIds = this.mapArrayIds(this.selectedAssets);
      this.loadingModules = true;
      return ModuleService.getModulesByAssetIds({ assetsIds })
        .then((response) => {
          this.modules = response;
          this.selectedModules = this.syncSelectedWithList(
            this.selectedModules,
            this.modules,
          );
        })
        .finally(() => {
          this.loadingModules = false;
        });
    },
    onRemoveAllModules() {
      this.selectedModules = [];
      this.updateFilter();
    },
    onUpdateModules() {
      this.updateFilter();
    },
  },
  watch: {
    filterObjects: {
      handler() {
        const fo = this.filterObjects || {};

        this.selectedCompanies = this.syncSelectedWithList(
          fo.companies || [],
          this.companies,
        );
        this.selectedRegional = fo.regional || [];
        this.selectedInstallations = fo.installations || [];
        this.selectedAssets = fo.assets || [];
        this.selectedModules = fo.modules || [];

        if (this.selectedCompanies.length) {
          this.getRegional();
          this.getInstallations(false);
        } else if (this.selectedRegional.length) {
          this.getInstallations(false);
        }
        if (this.selectedInstallations.length) {
          this.getAssets();
        }

        if (this.selectedAssets.length) {
          if (this.hasSensorField) {
            this.getSensors();
          }

          if (this.hasModuleField) {
            this.getModules();
          }
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="sass" scoped>
.reports-filter
  padding: 0

  &__row .col:last-child:not(:nth-child(even))
    max-width: 100%
    flex: 0 0 100%
</style>
